import styled, { createGlobalStyle } from 'styled-components';
import {
  white,
  sevenGreenPrimary,
  sevenGreenDark,
  sevenYellow,
  sevenCyan,
  sevenCyanLight,
  sevenNavy,
  sevenSurfaceGreen,
  sevenSurfaceYellow,
  sevenTextPrimary,
  sevenTextSoft,
  sevenBorder,
} from 'styles/colorProvider';

export const PrintStyles = createGlobalStyle`
  body {
    margin: 0;
    padding: 24px 12px;
    background: #eef1f4;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: ${sevenTextPrimary};
    font-size: 13px;
    line-height: 1.5;
  }

  @media print {
    body {
      background: ${white};
      padding: 0;
      font-size: 10.5px;
    }

    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    @page {
      margin: 12mm;
    }
  }
`;

export const LoadingArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-top: 30px;
`;

export const ButtonArea = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  max-width: 820px;
  margin: 0 auto 8px;

  @media print {
    display: none;
  }

  button {
    background-color: ${sevenGreenPrimary};
    margin-left: 0;
  }
`;

export const Sheet = styled.div`
  max-width: 820px;
  margin: 0 auto;
  background: ${white};
  box-shadow: 0 2px 24px rgba(8, 32, 51, 0.1);
  border-radius: 4px;
  overflow: hidden;

  @media print {
    box-shadow: none;
    max-width: 100%;
    border-radius: 0;
  }

  @media (max-width: 720px) {
    border-radius: 0;
    box-shadow: none;
  }
`;

export const DocumentHeader = styled.header`
  padding: 28px 34px 22px;
  border-bottom: 3px solid ${sevenGreenPrimary};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;

  h1 {
    font-size: 19px;
    font-weight: 800;
    color: ${sevenNavy};
    letter-spacing: -0.3px;
    line-height: 1.25;
    margin: 0;
  }

  @media (max-width: 720px) {
    padding-left: 18px;
    padding-right: 18px;
    gap: 14px;
  }
`;

export const DocumentSubtitle = styled.p`
  font-size: 12px;
  color: ${sevenTextSoft};
  margin: 6px 0 0;
  max-width: 420px;
`;

export const Logo = styled.img`
  width: 172px;
  height: auto;
  flex-shrink: 0;
  display: block;

  @media (max-width: 720px) {
    width: 132px;
  }
`;

export const Field = styled.div`
  strong {
    display: block;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: ${sevenTextSoft};
    font-weight: 700;
    margin-bottom: 2px;
  }

  span {
    font-size: 12.5px;
    color: ${sevenTextPrimary};
    font-weight: 500;
  }

  @media print {
    break-inside: avoid;
  }
`;

export const MonoValue = styled.span`
  && {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11.5px;
    font-weight: 500;
  }
`;

export const IdentificationBand = styled.section`
  background: ${sevenSurfaceGreen};
  border-bottom: 1px solid ${sevenBorder};
  padding: 16px 34px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px 20px;

  strong {
    color: ${sevenGreenDark};
    letter-spacing: 0.7px;
  }

  span {
    font-weight: 600;
  }

  @media (max-width: 720px) {
    padding-left: 18px;
    padding-right: 18px;
    grid-template-columns: 1fr 1fr;
  }
`;

export const Badge = styled.span`
  && {
    display: inline-block;
    background: ${sevenGreenPrimary};
    color: ${white};
    font-size: 10.5px;
    font-weight: 700;
    padding: 2px 9px;
    border-radius: 20px;
    letter-spacing: 0.4px;
  }
`;

const sectionBackground = {
  yellow: sevenYellow,
  cyan: sevenCyan,
};

export const SectionTitle = styled.h2`
  background: ${({ $variant }) => sectionBackground[$variant] || sevenNavy};
  color: ${({ $variant }) => ($variant === 'yellow' ? sevenNavy : white)};
  padding: 7px 34px;
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;

  @media print {
    break-after: avoid;
  }

  @media (max-width: 720px) {
    padding-left: 18px;
    padding-right: 18px;
  }
`;

export const SectionBody = styled.div`
  padding: 16px 34px 20px;

  @media (max-width: 720px) {
    padding-left: 18px;
    padding-right: 18px;
  }
`;

export const Grid = styled.div`
  display: grid;
  gap: 12px 22px;
  grid-template-columns: repeat(${({ $cols }) => $cols || 2}, 1fr);

  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  thead th {
    text-align: left;
    background: ${sevenSurfaceGreen};
    color: ${sevenGreenDark};
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    padding: 8px 10px;
    border-bottom: 1.5px solid ${sevenGreenPrimary};
  }

  tbody td {
    padding: 9px 10px;
    border-bottom: 1px solid ${sevenBorder};
    vertical-align: top;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  td.num,
  th.num {
    text-align: right;
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 500;
    white-space: nowrap;
  }

  td.service {
    font-weight: 600;
    color: ${sevenNavy};
  }

  .empty {
    color: ${sevenTextSoft};
  }

  @media print {
    tr {
      break-inside: avoid;
    }
  }

  @media (max-width: 720px) {
    font-size: 11px;

    thead th,
    tbody td {
      padding: 7px 6px;
    }
  }
`;

export const Totals = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border: 1px solid ${sevenBorder};
  border-radius: 6px;
  overflow: hidden;
  margin-top: 4px;

  > div {
    padding: 12px 14px;
    border-right: 1px solid ${sevenBorder};
  }

  > div:last-child {
    border-right: none;
  }

  strong {
    display: block;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: ${sevenTextSoft};
    font-weight: 700;
    margin-bottom: 4px;
  }

  span {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 14px;
    font-weight: 600;
    color: ${sevenTextPrimary};
  }

  .highlight {
    background: ${sevenGreenPrimary};
    border-right: none;
  }

  .highlight strong {
    color: rgba(255, 255, 255, 0.85);
  }

  .highlight span {
    color: ${white};
    font-size: 17px;
    font-weight: 700;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr 1fr;

    > div {
      border-right: none;
      border-bottom: 1px solid ${sevenBorder};
    }
  }
`;

export const PaymentDetails = styled.div`
  display: flex;
  gap: 28px;
  flex-wrap: wrap;
  margin-top: 12px;
  background: ${sevenSurfaceYellow};
  border-left: 3px solid ${sevenYellow};
  padding: 10px 14px;
  border-radius: 0 4px 4px 0;
`;

export const ServiceDescription = styled.div`
  h3 {
    font-size: 13px;
    font-weight: 700;
    color: ${sevenNavy};
    margin: 16px 0 5px;
    padding-left: 11px;
    border-left: 3px solid ${sevenGreenPrimary};

    @media print {
      break-after: avoid;
    }
  }

  h3:first-child {
    margin-top: 0;
  }

  p {
    font-size: 11.5px;
    line-height: 1.62;
    color: #31404f;
    text-align: justify;
    margin: 0;
  }
`;

export const GuarantorBox = styled.div`
  background: ${sevenCyanLight};
  padding: 16px 34px 20px;

  strong {
    color: #0d7f92;
  }

  @media (max-width: 720px) {
    padding-left: 18px;
    padding-right: 18px;
  }
`;

export const GuarantorNotice = styled.p`
  font-size: 11.5px;
  line-height: 1.6;
  color: #0d5a68;
  border-top: 1px solid rgba(20, 184, 209, 0.35);
  margin: 12px 0 0;
  padding-top: 12px;
  text-align: justify;

  b {
    color: ${sevenNavy};
  }
`;

export const Alert = styled.div`
  margin: 16px 34px 26px;
  padding: 12px 15px;
  border-radius: 6px;
  background: ${sevenSurfaceYellow};
  border: 1px solid ${sevenYellow};
  font-size: 11.5px;
  line-height: 1.6;
  color: #5c4708;

  b {
    color: ${sevenNavy};
  }

  @media print {
    break-inside: avoid;
  }

  @media (max-width: 720px) {
    margin-left: 18px;
    margin-right: 18px;
  }
`;

export const Note = styled.p`
  font-size: 10.5px;
  color: ${sevenTextSoft};
  line-height: 1.55;
  margin: 8px 0 0;

  b {
    color: ${sevenNavy};
  }
`;

export const Footer = styled.footer`
  border-top: 3px solid ${sevenGreenPrimary};
  background: ${sevenNavy};
  color: ${white};
  padding: 16px 34px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  p {
    font-size: 10.5px;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.78);
    max-width: 520px;
    margin: 0;
  }

  b {
    color: ${white};
  }

  @media (max-width: 720px) {
    padding-left: 18px;
    padding-right: 18px;
  }
`;

export const FooterLogo = styled.img`
  width: 132px;
  height: auto;
  flex-shrink: 0;
  display: block;
  background: ${white};
  padding: 9px 13px;
  border-radius: 8px;
  box-sizing: content-box;
`;
